/**
 * Ce que l'écran montre à un niveau donné : un seul décor, et deux faits.
 *
 * ## Un décor et un seul
 *
 * Le parcours a longtemps eu deux écrans sous une seule identité. Aux quatre
 * premiers niveaux le **couloir de chute** restait fermé : la scène couvrait le
 * clavier du roll, et le milieu de l'écran nommait l'accord en grand au lieu de
 * le dessiner. À partir du cinquième il s'ouvrait, les notes tombaient — la
 * main gauche seule, puis les deux — et les grands noms cédaient la place à une
 * bande de clavier.
 *
 * Les niveaux 5 à 9 ne sont pas opérationnels. Le second décor n'était donc
 * atteint par personne, et il coûtait une mise en page en double dans
 * `harmony-scene.tsx` : seize branches « couloir fermé / couloir ouvert », deux
 * jeux d'objets au milieu de l'écran, et une carte de frontière pour annoncer
 * le passage de l'un à l'autre. Tout cela est parti, et avec lui le couloir, le
 * clavier de scène, le ruban de l'unité, l'annonce du bandeau et
 * l'enchaînement à la main.
 *
 * Les neuf niveaux se jouent donc dans le décor des quatre premiers, qui est le
 * seul qui ait jamais été joué. Ce n'est pas un repli : c'est le décor où l'on
 * **prend un accord** au lieu de jouer des notes, et c'est ce que le parcours
 * demande à tous ses niveaux tant que la chute n'est pas branchée.
 *
 * Le jour où le niveau 5 ouvrira pour de bon, la mise en page à couloir ouvert
 * se reprend dans l'historique plutôt que dans ce fichier : une seconde mise en
 * page qu'aucun écran n'atteint n'est pas une avance, c'est une deuxième
 * réponse à « à quoi ressemble le parcours » que personne ne peut vérifier.
 *
 * ## Ce qui reste : le contrat, et la couture
 *
 * Deux faits sur un niveau, et ils ne se lisent pas de la même source. Le
 * contrat se lit sur ce que l'analyseur répond, la couture sur l'indice
 * canonique du niveau dans `LADDER_SHAPE`. Voir chacun.
 */

import type { Level } from "@/lib/levels/client";

/**
 * La ligne de contrat, en bas à gauche du bandeau.
 *
 * C'est la seule chose de l'écran qui sépare un modèle assumé d'un mensonge sur
 * la partition, et une version antérieure du programme s'est cassée exactement
 * là. Elle se lit donc sur les faits du niveau, jamais sur son numéro : c'est
 * l'analyseur qui décide où la partition prend la main, et une table de
 * numéros écrite ici serait une seconde réponse à cette question.
 */
export function contractLine(level: Pick<Level, "realisation" | "scaffolded">): string {
  if (level.realisation === "modele") {
    return "MODÈLE";
  }

  if (level.scaffolded) {
    return "PARTITION · MAIN DROITE AU MODÈLE";
  }

  return "PARTITION";
}

/**
 * Le niveau où les mains changent de place, et le seul.
 *
 * La basse publiée : c'est là que la gauche quitte la fondamentale du modèle
 * pour la ligne du morceau, et que la droite monte si la basse écrite vient lui
 * prendre ses touches. Elle tombe entre deux niveaux, donc sur un écran
 * immobile : la couture se lit avant de jouer, jamais pendant.
 *
 * L'indice canonique et non le rang affiché, pour la même raison qu'`isSasRung`
 * : `RungMap` renumérote sans trou, et un morceau à sept niveaux coudrait au
 * mauvais endroit.
 */
export function isSeamRung(index: number): boolean {
  return index === 3;
}
